import { required } from "ra-core";
import { useWatch } from "react-hook-form";
import { DateTimeInput } from "@/components/admin";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { EventDealStaff } from "./EventDealStaff";

/** An event must concern a company or a contact (the database enforces it too). */
const requireCompanyOrContact = (
  _value: unknown,
  values: { company_id?: unknown; contact_id?: unknown },
) =>
  values?.company_id || values?.contact_id
    ? undefined
    : "resources.events.validation.company_or_contact";

const validateEnd = (value: unknown, values: { start_at?: string }) =>
  value &&
  values?.start_at &&
  new Date(String(value)) < new Date(values.start_at)
    ? "resources.events.validation.end_after_start"
    : undefined;

export const EventInputs = () => {
  const { eventTypes } = useConfigurationContext();
  const companyId = useWatch({ name: "company_id" });
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="name"
        validate={required()}
        helperText={false}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DateTimeInput
          source="start_at"
          validate={required()}
          helperText={false}
        />
        <DateTimeInput
          source="end_at"
          validate={validateEnd}
          helperText={false}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectInput
          source="type"
          choices={eventTypes}
          optionText="label"
          optionValue="value"
          helperText={false}
        />
        <TextInput source="organizer_name" helperText={false} />
        <NumberInput source="price" min={0} step={50} helperText={false} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteInput
            label="resources.events.fields.company_id"
            optionText="name"
            helperText={false}
            validate={requireCompanyOrContact}
            modal
          />
        </ReferenceInput>
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput
            label="resources.events.fields.contact_id"
            optionText={contactOptionText}
            helperText={false}
            validate={requireCompanyOrContact}
            modal
          />
        </ReferenceInput>
      </div>
      <div className="flex flex-col gap-2">
        <ReferenceInput
          source="deal_id"
          reference="deals"
          // The company's opportunities once a company is chosen.
          filter={companyId ? { company_id: companyId } : {}}
          sort={{ field: "name", order: "ASC" }}
        >
          <AutocompleteInput
            label="resources.events.fields.deal_id"
            optionText="name"
            helperText={false}
            modal
          />
        </ReferenceInput>
        <EventDealStaff />
      </div>
      <ReferenceArrayInput
        source="partner_ids"
        reference="partners"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteArrayInput
          label="resources.events.fields.partner_ids"
          optionText="name"
          helperText={false}
        />
      </ReferenceArrayInput>
      <TextInput
        source="deliverables"
        multiline
        // The textarea grows with its content; start it tall enough for a brief.
        inputClassName="min-h-40"
        helperText={false}
      />
    </div>
  );
};
